<template>
  <v-container fluid class="pa-4">
    <!-- Page Header -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="48" class="mr-4">
              <v-icon color="white" size="24">mdi-security-network</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text--primary mb-1">
                {{ $t('role.title.permission_manager') }}
              </h1>
              <p class="text--secondary text-subtitle-1 mb-0">
                {{ $t('role.permission_manager.create_new') }}
              </p>
            </div>
          </div>
          <v-btn
            color="primary"
            large
            elevation="2"
            @click="showCreatePermissionModal = true"
            v-can="'permission.create'"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('permission.button.create') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card color="primary" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ permissions.length }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('permission.label.total_permissions') }}</div>
              </div>
              <v-avatar color="primary" size="48">
                <v-icon size="24">mdi-security-network</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="success" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ assignedPermissions }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('permission.label.assigned_permissions') }}</div>
              </div>
              <v-avatar color="success" size="48">
                <v-icon size="24">mdi-check-circle</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="info" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ permissionCategories.length }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('permission.label.categories') }}</div>
              </div>
              <v-avatar color="info" size="48">
                <v-icon size="24">mdi-folder-multiple</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card color="warning" dark class="elevation-4">
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold">{{ unassignedPermissions }}</div>
                <div class="text-subtitle-1 opacity-90">{{ $t('permission.label.unassigned_permissions') }}</div>
              </div>
              <v-avatar color="warning" size="48">
                <v-icon size="24">mdi-alert-circle</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Permission Categories -->
    <v-card class="elevation-2">
      <v-card-title class="pa-4">
        <v-icon color="primary" class="mr-2">mdi-folder-multiple</v-icon>
        {{ $t('permission.label.categories') }}
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('text.search_placeholder')"
          single-line
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </v-card-title>

      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
        class="px-4"
        show-arrows
      >
        <v-tab
          v-for="category in filteredCategories"
          :key="category.name"
          :value="category.name"
        >
          <v-icon left small>mdi-folder</v-icon>
          {{ category.name }}
          <v-chip
            x-small
            color="primary"
            class="ml-2"
          >
            {{ category.permissions.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item
          v-for="category in filteredCategories"
          :key="category.name"
          :value="category.name"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between mb-4">
              <h3 class="text-h6 font-weight-medium">
                {{ category.name }} {{ $t('permission.label.permissions') }}
              </h3>
              <v-chip color="primary" outlined>
                {{ category.permissions.length }} {{ $t('permission.label.total') }}
              </v-chip>
            </div>

            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="4"
                lg="3"
                v-for="permission in category.permissions"
                :key="permission.id"
              >
                <v-card
                  class="permission-card elevation-1"
                  :class="{ 'permission-card--assigned': isPermissionAssigned(permission.id) }"
                  hover
                >
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <v-avatar
                        :color="isPermissionAssigned(permission.id) ? 'success' : 'grey lighten-2'"
                        size="32"
                      >
                        <v-icon
                          :color="isPermissionAssigned(permission.id) ? 'white' : 'grey darken-1'"
                          size="16"
                        >
                          {{ getPermissionIcon(permission.name) }}
                        </v-icon>
                      </v-avatar>
                      <v-chip
                        :color="getGuardColor(permission.guard_name)"
                        x-small
                        outlined
                      >
                        {{ permission.guard_name }}
                      </v-chip>
                    </div>

                    <div class="permission-name mb-2">
                      {{ permission.name }}
                    </div>

                    <div class="text-caption text--secondary mb-3">
                      {{ permission.description || $t('permission.label.no_description') }}
                    </div>

                    <div class="d-flex align-center justify-space-between">
                      <v-chip
                        v-if="isPermissionAssigned(permission.id)"
                        color="success"
                        x-small
                        outlined
                      >
                        <v-icon left x-small>mdi-check</v-icon>
                        {{ $t('permission.label.assigned') }}
                      </v-chip>
                      <v-chip
                        v-else
                        color="warning"
                        x-small
                        outlined
                      >
                        <v-icon left x-small>mdi-alert</v-icon>
                        {{ $t('permission.label.unassigned') }}
                      </v-chip>

                      <v-btn
                        icon
                        x-small
                        color="primary"
                        @click="viewPermissionRoles(permission)"
                      >
                        <v-icon x-small>mdi-eye</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Empty State for Category -->
            <div v-if="category.permissions.length === 0" class="text-center py-8">
              <v-icon color="grey lighten-1" size="64">mdi-folder-open</v-icon>
              <p class="text--secondary mt-2">{{ $t('permission.label.no_permissions_in_category') }}</p>
            </div>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Create Permission Dialog -->
    <v-dialog
      v-model="showCreatePermissionModal"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title class="pa-4">
          <v-icon color="primary" class="mr-2">mdi-plus</v-icon>
          {{ $t('permission.title.create') }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="permissionForm" v-model="formValid">
            <v-text-field
              v-model="newPermission.name"
              :label="$t('permission.label.name')"
              :rules="permissionRules.name"
              :placeholder="$t('permission.label.name_placeholder')"
              required
              outlined
              class="mb-3"
            ></v-text-field>

            <v-textarea
              v-model="newPermission.description"
              :label="$t('permission.label.description')"
              :placeholder="$t('permission.label.description_placeholder')"
              outlined
              rows="3"
              class="mb-3"
            ></v-textarea>

            <v-select
              v-model="newPermission.guard_name"
              :label="$t('permission.label.guard_name')"
              :rules="permissionRules.guard_name"
              :items="guardOptions"
              outlined
              required
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeCreateModal"
            :disabled="creating"
          >
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="createPermission"
            :loading="creating"
            :disabled="!formValid"
          >
            {{ $t('permission.button.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permission Roles Dialog -->
    <v-dialog
      v-model="showRolesModal"
      max-width="800px"
    >
      <v-card>
        <v-card-title class="pa-4">
          <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
          {{ $t('permission.label.assigned_roles') }}
        </v-card-title>

        <v-card-text class="pa-4">
          <div v-if="selectedPermission" class="mb-4">
            <v-chip color="primary" outlined class="mb-2">
              <v-icon left small>mdi-security-network</v-icon>
              {{ selectedPermission.name }}
            </v-chip>
            <div class="text-caption text--secondary">
              {{ selectedPermission.description || $t('permission.label.no_description') }}
            </div>
          </div>

          <v-data-table
            :headers="roleHeaders"
            :items="permissionRoles"
            :items-per-page="10"
            class="elevation-0"
          >
            <template slot="item.name" slot-scope="{ item }">
              <div class="d-flex align-center">
                <v-avatar :color="getRoleColor(item.name)" size="24" class="mr-2">
                  <v-icon color="white" size="12">mdi-shield</v-icon>
                </v-avatar>
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>

            <template slot="item.guard_name" slot-scope="{ item }">
              <v-chip
                :color="getGuardColor(item.guard_name)"
                x-small
                outlined
              >
                {{ item.guard_name }}
              </v-chip>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-4">
                <v-icon color="grey lighten-1" size="48">mdi-account-off</v-icon>
                <p class="text--secondary mt-2">{{ $t('permission.label.no_roles_assigned') }}</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showRolesModal = false">
            {{ $t('button.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'PermissionManager',
  data () {
    return {
      permissions: [],
      roles: [],
      activeTab: null,
      search: '',
      showCreatePermissionModal: false,
      showRolesModal: false,
      selectedPermission: null,
      permissionRoles: [],
      creating: false,
      formValid: false,
      newPermission: {
        name: '',
        description: '',
        guard_name: 'api'
      },
      guardOptions: [
        { text: 'API', value: 'api' },
        { text: 'Web', value: 'web' },
        { text: 'Admin', value: 'admin' }
      ],
      permissionRules: {
        name: [
          v => !!v || this.$t('permission.validation.name.required'),
          v => (v && v.length >= 3) || this.$t('permission.validation.name.length')
        ],
        guard_name: [
          v => !!v || this.$t('permission.validation.guard_name.required')
        ]
      }
    }
  },
  computed: {
    permissionCategories () {
      const categories = {}

      this.permissions.forEach(permission => {
        const category = permission.name.split('.')[0] // Get entity prefix as category
        const categoryName = category.charAt(0).toUpperCase() + category.slice(1)

        if (!categories[categoryName]) {
          categories[categoryName] = {
            name: categoryName,
            permissions: []
          }
        }
        categories[categoryName].permissions.push(permission)
      })

      // Sort permissions within each category
      Object.keys(categories).forEach(categoryName => {
        categories[categoryName].permissions.sort((a, b) => a.name.localeCompare(b.name))
      })

      return Object.values(categories).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredCategories () {
      if (!this.search) return this.permissionCategories

      const searchLower = this.search.toLowerCase()
      return this.permissionCategories.filter(category =>
        category.name.toLowerCase().includes(searchLower) ||
        category.permissions.some(p => p.name.toLowerCase().includes(searchLower))
      )
    },
    assignedPermissions () {
      return this.permissions.filter(permission => this.isPermissionAssigned(permission.id)).length
    },
    unassignedPermissions () {
      return this.permissions.filter(permission => !this.isPermissionAssigned(permission.id)).length
    },
    roleHeaders () {
      return [
        { text: this.$t('role.label.name'), value: 'name' },
        { text: this.$t('role.label.guard_name'), value: 'guard_name' },
        { text: this.$t('role.label.description'), value: 'description' }
      ]
    }
  },
  methods: {
    async loadData () {
      try {
        await this.withLoading(async () => {
          const [permissionsData, rolesData] = await Promise.all([
            this.$permissionService.getPermissions(),
            this.$roleService.getRoles()
          ])

          this.permissions = permissionsData
          this.roles = rolesData

          // Set active tab to first category
          if (this.permissionCategories.length > 0) {
            this.activeTab = this.permissionCategories[0].name
          }
        }, 'loading')
      } catch (error) {
        console.log('Component caught error in loadData:', error)
      }
    },
    isPermissionAssigned (permissionId) {
      // Check if permission is assigned to any role
      return this.roles.some(role =>
        role.permissions && role.permissions.some(p => p.id === permissionId)
      )
    },
    async viewPermissionRoles (permission) {
      this.selectedPermission = permission

      // Find roles that have this permission
      this.permissionRoles = this.roles.filter(role =>
        role.permissions && role.permissions.some(p => p.id === permission.id)
      )

      this.showRolesModal = true
    },
    async createPermission () {
      if (this.$refs.permissionForm.validate()) {
        this.creating = true
        try {
          await this.$permissionService.createPermission(this.newPermission)
          this.$message.success(this.$t('permission.message.created'))
          this.closeCreateModal()
          await this.loadData()
        } catch (error) {
          console.log('Error creating permission:', error)
        } finally {
          this.creating = false
        }
      }
    },
    closeCreateModal () {
      this.showCreatePermissionModal = false
      this.newPermission = { name: '', description: '', guard_name: 'api' }
      this.$refs.permissionForm?.reset()
    },
    getPermissionIcon (permissionName) {
      const icons = {
        view: 'mdi-eye',
        create: 'mdi-plus',
        edit: 'mdi-pencil',
        delete: 'mdi-delete',
        manage: 'mdi-cog',
        assign: 'mdi-account-plus',
        export: 'mdi-download',
        import: 'mdi-upload'
      }

      const action = permissionName.split('.').slice(1).join('.').toLowerCase()
      return icons[action] || icons[action.split('-')[0]] || 'mdi-security-network'
    },
    getGuardColor (guard) {
      const colors = {
        api: 'primary',
        web: 'secondary',
        admin: 'success'
      }
      return colors[guard] || 'grey'
    },
    getRoleColor (roleName) {
      const colors = ['primary', 'success', 'warning', 'info', 'error', 'purple', 'pink', 'indigo']
      const index = roleName.toLowerCase().charCodeAt(0) % colors.length
      return colors[index]
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('role.title.permission_manager'))
    await this.loadData()
  }
}
</script>

<style scoped>
.search-field {
  max-width: 300px;
}

.permission-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  cursor: pointer;
}

.permission-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

.permission-card--assigned {
  border-left: 4px solid #4caf50;
}

.permission-name {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
}

.v-card {
  border-radius: 12px;
}

.v-tabs {
  border-bottom: 1px solid #e0e0e0;
}

.v-tab {
  text-transform: none;
  font-weight: 500;
}

.v-chip {
  border-radius: 6px;
}

.v-data-table {
  border-radius: 8px;
}

.v-avatar {
  border-radius: 8px;
}
</style>
